<template>
  <v-container fluid class="pa-6 data-cleansing-view">
    <page-bar title="Orphaned Attribute Values" subtitle="Review and remove orphaned attribute values">
      <template v-slot:actions>
        <v-btn small color="primary" dark class="mr-2" :loading="loading" @click="GetOrphanedAttributeValues">
          <v-icon left>mdi-refresh</v-icon>
          Refresh
        </v-btn>
        <v-btn
          small
          color="red darken-1"
          dark
          :disabled="!orphanedAttributeValues.length || deletingAll"
          :loading="deletingAll"
          @click="showDeleteAllDialog = true"
        >
          <v-icon left>mdi-delete-sweep</v-icon>
          Delete All
        </v-btn>
      </template>
    </page-bar>

    <v-row class="mt-4" dense>
      <v-col cols="12" md="4">
        <v-sheet rounded class="pa-3 stat-tile">
          <div class="caption grey--text text--darken-1">Scoped Market</div>
          <div class="subtitle-1 font-weight-medium">{{ selectedMarketLabel }}</div>
        </v-sheet>
      </v-col>
      <v-col cols="12" md="4">
        <v-sheet rounded class="pa-3 stat-tile">
          <div class="caption grey--text text--darken-1">Orphaned Values</div>
          <div class="subtitle-1 font-weight-bold">{{ orphanedAttributeValues.length }}</div>
        </v-sheet>
      </v-col>
      <v-col cols="12" md="4">
        <v-sheet rounded class="pa-3 stat-tile">
          <div class="caption grey--text text--darken-1">Last Loaded</div>
          <div class="subtitle-1 font-weight-medium">{{ lastLoadedAt }}</div>
        </v-sheet>
      </v-col>
    </v-row>

    <v-row class="mt-2">
      <v-col cols="12">
        <v-card>
          <v-card-text class="pb-2">
            <v-alert
              v-if="errorMessage"
              type="error"
              outlined
              dense
              dismissible
              @input="clearError"
            >
              {{ errorMessage }}
            </v-alert>

            <Loading v-if="loading" :value="'Loading orphaned attribute values...'"></Loading>

            <template v-else>
              <v-row align="center" dense class="mb-2">
                <v-col cols="12" md="8">
                  <div class="subtitle-2 font-weight-medium">Attribute Values</div>
                  <div class="caption grey--text text--darken-1">
                    Values listed below are not currently in use and can be removed.
                  </div>
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="search"
                    dense
                    outlined
                    clearable
                    hide-details
                    prepend-inner-icon="mdi-magnify"
                    label="Search value name"
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-data-table
                :headers="headers"
                :items="orphanedAttributeValues"
                :search="search"
                item-key="id"
                class="elevation-1"
                dense
                :items-per-page="10"
              >
                <template v-slot:item.name="{ item }">
                  <div class="font-weight-medium">{{ item.name }}</div>
                  <div class="caption grey--text">{{ item.id }}</div>
                </template>

                <template v-slot:item.createdDate="{ item }">
                  {{ formatDate(item.createdDate) }}
                </template>

                <template v-slot:item.modifiedDate="{ item }">
                  {{ formatDate(item.modifiedDate) }}
                </template>

                <template v-slot:item.actions="{ item }">
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        color="red darken-1"
                        :disabled="deletingAll"
                        v-bind="attrs"
                        v-on="on"
                        @click="promptDelete(item)"
                      >
                        <v-icon v-if="deletingValueId === item.id">mdi-loading mdi-spin</v-icon>
                        <v-icon v-else>mdi-delete</v-icon>
                      </v-btn>
                    </template>
                    <span>Delete orphaned value</span>
                  </v-tooltip>
                </template>

                <template v-slot:no-data>
                  <div class="py-8 text-center">
                    <v-icon color="green" class="mb-2">mdi-check-circle-outline</v-icon>
                    <div>No orphaned attribute values found for your current market.</div>
                  </div>
                </template>
              </v-data-table>
            </template>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="showDeleteDialog" width="500px" persistent>
      <v-card>
        <v-card-title class="pa-3 headline">Delete Attribute Value</v-card-title>
        <v-card-text>
          Are you sure you want to delete
          <strong>{{ selectedValue ? selectedValue.name : "this value" }}</strong>?
          This action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" dark @click="showDeleteDialog = false">Cancel</v-btn>
          <v-btn color="red darken-1" dark :loading="Boolean(deletingValueId)" @click="confirmDelete">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showDeleteAllDialog" width="520px" persistent>
      <v-card>
        <v-card-title class="pa-3 headline">Delete All Orphaned Values</v-card-title>
        <v-card-text>
          This will delete <strong>{{ orphanedAttributeValues.length }}</strong> orphaned value(s)
          for the current market. Continue?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" dark @click="showDeleteAllDialog = false">Cancel</v-btn>
          <v-btn color="red darken-1" dark :loading="deletingAll" @click="confirmDeleteAll">Delete All</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapActions, mapState, mapMutations } from "vuex";
import Loading from "../shared/Loading";
import PageBar from "../shared/PageBar";

export default {
  name: "DataCleansing",
  components: {
    Loading,
    PageBar,
  },
  data: () => ({
    showDeleteDialog: false,
    showDeleteAllDialog: false,
    selectedValue: null,
    search: "",
    loadedAt: null,
    headers: [
      { text: "Value", value: "name", sortable: true },
      { text: "Created", value: "createdDate", sortable: true },
      { text: "Last Modified", value: "modifiedDate", sortable: true },
      { text: "", value: "actions", sortable: false, width: "80px" },
    ],
  }),
  computed: {
    ...mapState("datacleansingstate", [
      "orphanedAttributeValues",
      "loading",
      "deletingValueId",
      "deletingAll",
      "error",
    ]),
    ...mapState("assetxpstate", ["selectedMarketId", "markets"]),
    errorMessage() {
      return this.error;
    },
    selectedMarketLabel() {
      if (!this.selectedMarketId) {
        return "No market selected";
      }

      const market = (this.markets || []).find((entry) => entry && entry.id === this.selectedMarketId);
      return market ? market.name : this.selectedMarketId;
    },
    lastLoadedAt() {
      return this.loadedAt ? this.formatDate(this.loadedAt) : "Not loaded yet";
    },
  },
  mounted() {
    this.loadValues();
  },
  methods: {
    ...mapActions("datacleansingstate", [
      "GetOrphanedAttributeValues",
      "DeleteOrphanedAttributeValue",
      "DeleteAllOrphanedAttributeValues",
    ]),
    ...mapMutations("datacleansingstate", ["SetError"]),
    async loadValues() {
      await this.GetOrphanedAttributeValues();
      this.loadedAt = new Date();
    },
    formatDate(value) {
      if (!value) {
        return "-";
      }

      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) {
        return "-";
      }

      return parsed.toLocaleString();
    },
    promptDelete(value) {
      this.selectedValue = value;
      this.showDeleteDialog = true;
    },
    async confirmDelete() {
      if (!this.selectedValue || !this.selectedValue.id) {
        this.showDeleteDialog = false;
        return;
      }

      try {
        await this.DeleteOrphanedAttributeValue(this.selectedValue.id);
        this.loadedAt = new Date();
      } finally {
        this.showDeleteDialog = false;
        this.selectedValue = null;
      }
    },
    async confirmDeleteAll() {
      try {
        await this.DeleteAllOrphanedAttributeValues();
        this.loadedAt = new Date();
      } finally {
        this.showDeleteAllDialog = false;
      }
    },
    clearError() {
      this.SetError(null);
    },
  },
};
</script>

<style scoped>
.data-cleansing-view .stat-tile {
  background: #f6f8fb;
  border: 1px solid #e5e8ef;
}

.data-cleansing-view .v-data-table .caption {
  line-height: 1.2;
}
</style>
